<script setup lang="ts">
import type { NutritionRecordSummary } from '~/server/api/area-riservata/nutrition-records.get';
import {
	DEFAULT_NUTRITION_CHART_PARAM,
	getParamLabel,
	getParamUnit,
	type NutritionChartParamKey,
} from './nutrition-chart-params';

const props = withDefaults(
	defineProps<{
		records: NutritionRecordSummary[];
		paramKey?: NutritionChartParamKey;
		accentColor?: string;
	}>(),
	{ paramKey: DEFAULT_NUTRITION_CHART_PARAM, accentColor: '#2ECDA7' }
);

const canvasRef = ref<HTMLCanvasElement | null>(null);
let chartInstance: import('chart.js').Chart<'line'> | null = null;

const sortedRecords = computed(() => {
	const list = [...props.records].filter(
		(r) => r.appointment_date != null && getValue(r, props.paramKey) != null
	);
	list.sort((a, b) => {
		const da = new Date(a.appointment_date!).getTime();
		const db = new Date(b.appointment_date!).getTime();
		return da - db;
	});
	return list;
});

function getValue(r: NutritionRecordSummary, key: NutritionChartParamKey): number | null {
	const v = (r as Record<string, unknown>)[key];
	if (v == null) return null;
	const n = Number(v);
	return Number.isNaN(n) ? null : n;
}

/** Punti per il grafico: x = timestamp (ms) per spaziatura proporzionale ai giorni, y = valore */
const chartData = computed(() => {
	const list = sortedRecords.value;
	return list.map((r) => ({
		x: new Date(r.appointment_date!).getTime(),
		y: getValue(r, props.paramKey) as number,
	}));
});

function formatAxisDate(timestamp: number): string {
	return new Date(timestamp).toLocaleDateString('it-IT', {
		day: '2-digit',
		month: 'short',
		year: 'numeric',
	});
}

const hasData = computed(() => chartData.value.length > 0);
const yAxisLabel = computed(() => {
	const unit = getParamUnit(props.paramKey);
	return unit ? `${getParamLabel(props.paramKey)} (${unit})` : getParamLabel(props.paramKey);
});

async function initChart() {
	if (!import.meta.client || !canvasRef.value) return;
	const { default: Chart } = await import('chart.js/auto');
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
	const points = chartData.value;
	const xMin = points.length > 0 ? points[0].x : 0;
	const xMax = points.length > 1 ? points[points.length - 1].x : points[0]?.x ?? 0;
	// Con un solo punto evitiamo min === max (scale invalida): piccolo margine di 1 giorno
	const oneDayMs = 24 * 60 * 60 * 1000;
	const rangeMin = points.length === 1 ? xMin - oneDayMs : xMin;
	const rangeMax = points.length === 1 ? xMax + oneDayMs : xMax;

	chartInstance = new Chart(canvasRef.value, {
		type: 'line',
		data: {
			datasets: [
				{
					label: yAxisLabel.value,
					data: points,
					borderColor: props.accentColor,
					backgroundColor: `${props.accentColor}20`,
					fill: true,
					tension: 0.3,
					pointRadius: 4,
					pointHoverRadius: 6,
				},
			],
		},
		options: {
			responsive: true,
			maintainAspectRatio: false,
			interaction: { intersect: false, mode: 'index' },
			plugins: {
				legend: { display: false },
				tooltip: {
					callbacks: {
						title: (items) => {
							const raw = items[0]?.raw as { x: number; y: number };
							return raw?.x != null ? formatAxisDate(raw.x) : '';
						},
						label: (ctx) => {
							const unit = getParamUnit(props.paramKey);
							const v = ctx.parsed.y;
							const valueLine = unit ? `${v} ${unit}` : String(v);
							const data = ctx.dataset.data as { x: number; y: number }[];
							const prev = ctx.dataIndex > 0 ? data[ctx.dataIndex - 1]?.y : null;
							if (prev != null && prev !== 0 && typeof prev === 'number') {
								const pct = (((v - prev) / prev) * 100);
								const pctStr = pct >= 0 ? `+${pct.toFixed(1)}%` : `${pct.toFixed(1)}%`;
								return [valueLine, `${pctStr} visita precedente`];
							}
							return valueLine;
						},
					},
				},
			},
			scales: {
				x: {
					type: 'linear',
					min: rangeMin,
					max: rangeMax,
					grid: { display: false },
					ticks: {
						maxRotation: 45,
						maxTicksLimit: 8,
						callback: (value) => formatAxisDate(Number(value)),
					},
				},
				y: {
					beginAtZero: true,
					grid: { color: 'rgba(0,0,0,0.06)' },
					ticks: {
						callback: (value) => {
							const unit = getParamUnit(props.paramKey);
							return unit ? `${value} ${unit}` : String(value);
						},
					},
				},
			},
		},
	});
}

watch([() => props.paramKey, () => props.records, chartData], () => {
	if (!import.meta.client) return;
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
	if (!hasData.value) return;
	nextTick(() => initChart());
}, { flush: 'post' });

onMounted(() => {
	if (hasData.value) nextTick(() => initChart());
});

onBeforeUnmount(() => {
	if (chartInstance) {
		chartInstance.destroy();
		chartInstance = null;
	}
});
</script>

<template>
	<div class="rounded-none border border-gray-200 bg-white p-4">
		<div v-if="!hasData" class="flex h-[240px] items-center justify-center text-sm text-gray-500">
			Non ci sono dati da mostrare per questo parametro nelle visite disponibili.
		</div>
		<div v-else class="h-[280px] w-full">
			<canvas ref="canvasRef" class="h-full w-full" />
		</div>
	</div>
</template>
